import React, { useEffect, useState } from 'react';
import { CalendarPlus } from 'lucide-react';
import { api } from '../../lib/api';
import type { Meeting } from '../../types';
import {
  Spinner, EmptyState, Modal, Alert,
  inputClass, labelClass, primaryBtn, secondaryBtn,
} from '../../components/ui';
import MeetingsList from '../../components/MeetingsList';

interface MeetingForm {
  title: string;
  description: string;
  meeting_date: string;
  duration: number;
}

const emptyForm: MeetingForm = { title: '', description: '', meeting_date: '', duration: 30 };

export default function Meetings() {
  const [meetings, setMeetings] = useState<Meeting[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState<MeetingForm>(emptyForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const load = () => {
    setLoading(true);
    api
      .get<Meeting[]>('/meetings')
      .then(setMeetings)
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      await api.post('/meetings', form);
      setModalOpen(false);
      setForm(emptyForm);
      load();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const upcoming = meetings.filter((m) => m.status === 'scheduled');
  const past = meetings.filter((m) => m.status !== 'scheduled');

  return (
    <div>
      <div className="flex items-center justify-between gap-3 flex-wrap mb-6">
        <h1 className="text-2xl font-bold">Meetings</h1>
        <button onClick={() => { setError(''); setModalOpen(true); }} className={primaryBtn}>
          <CalendarPlus className="h-4 w-4" /> Schedule Meeting
        </button>
      </div>

      {loading ? (
        <Spinner label="Loading meetings..." />
      ) : (
        <div className="space-y-10">
          <div>
            <h2 className="text-lg font-semibold mb-4">Upcoming</h2>
            {upcoming.length === 0 ? <EmptyState message="No upcoming meetings." /> : <MeetingsList meetings={upcoming} />}
          </div>
          <div>
            <h2 className="text-lg font-semibold mb-4">Past / Other</h2>
            {past.length === 0 ? <EmptyState message="No past meetings." /> : <MeetingsList meetings={past} />}
          </div>
        </div>
      )}

      <Modal open={modalOpen} onClose={() => setModalOpen(false)} title="Schedule Meeting">
        {error && <Alert>{error}</Alert>}
        <form onSubmit={handleCreate} className="space-y-4">
          <div>
            <label className={labelClass}>Title *</label>
            <input className={inputClass} required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
          </div>
          <div>
            <label className={labelClass}>Description</label>
            <textarea className={inputClass} rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>Date & Time *</label>
              <input
                type="datetime-local"
                className={inputClass}
                required
                value={form.meeting_date}
                onChange={(e) => setForm({ ...form, meeting_date: e.target.value })}
              />
            </div>
            <div>
              <label className={labelClass}>Duration (minutes)</label>
              <input
                type="number"
                min={5}
                step={5}
                className={inputClass}
                value={form.duration}
                onChange={(e) => setForm({ ...form, duration: Number(e.target.value) })}
              />
            </div>
          </div>
          <p className="text-xs text-gray-500">
            A Jitsi meeting room will be created automatically. All users will be notified by email.
          </p>
          <div className="flex justify-end space-x-3">
            <button type="button" onClick={() => setModalOpen(false)} className={secondaryBtn}>Cancel</button>
            <button type="submit" disabled={saving} className={primaryBtn}>{saving ? 'Scheduling...' : 'Schedule'}</button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
